import * as React from "react"
import { createPortal } from "react-dom"
import { navigate } from "gatsby"
import {
  ArrowRight,
  Command,
  Download,
  ExternalLink,
  Search,
  UserPlus,
} from "lucide-react"

import { COMMANDS } from "../utils/site-actions"

const PENDING_EVENT_PREFIX = "wf-pending-event:"

function normalizePath(pathname) {
  if (!pathname || pathname === "/") return "/"
  return pathname.replace(/\/+$/, "")
}

export function dispatchSiteEvent(eventName, fallbackPath) {
  if (typeof window === "undefined") return

  const normalizedCurrent = normalizePath(window.location.pathname)
  const normalizedFallback = normalizePath(fallbackPath)

  if (fallbackPath && normalizedCurrent !== normalizedFallback) {
    try {
      window.sessionStorage.setItem(`${PENDING_EVENT_PREFIX}${eventName}`, "1")
    } catch (_) {
      // sessionStorage can be unavailable in private or restricted contexts.
    }
    navigate(fallbackPath)
    return
  }

  window.dispatchEvent(new CustomEvent(eventName))
}

export function consumePendingSiteEvent(eventName) {
  if (typeof window === "undefined") return false

  try {
    const key = `${PENDING_EVENT_PREFIX}${eventName}`
    const pending = window.sessionStorage.getItem(key) === "1"
    if (pending) window.sessionStorage.removeItem(key)
    return pending
  } catch (_) {
    return false
  }
}

function searchableText(command) {
  return [
    command.label,
    command.group,
    command.to,
    command.href,
    ...(command.keywords || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
}

function commandIcon(command) {
  if (command.id === "resume:request") return <Download size={16} />
  if (command.kind === "download") {
    return command.href?.endsWith(".vcf") ? <UserPlus size={16} /> : <Download size={16} />
  }
  if (command.kind === "external") return <ExternalLink size={16} />
  return <ArrowRight size={16} />
}

const CommandPalette = () => {
  const [mounted, setMounted] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState("")
  const [activeIndex, setActiveIndex] = React.useState(0)
  const openerRef = React.useRef(null)
  const dialogRef = React.useRef(null)
  const inputRef = React.useRef(null)

  const filteredCommands = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    if (!normalizedQuery) return COMMANDS
    return COMMANDS.filter(command => searchableText(command).includes(normalizedQuery))
  }, [query])

  const closePalette = React.useCallback(() => {
    setOpen(false)
    setQuery("")
    setActiveIndex(0)
    window.setTimeout(() => openerRef.current?.focus?.(), 0)
  }, [])

  const openPalette = React.useCallback(event => {
    openerRef.current = event?.currentTarget || document.activeElement
    setOpen(true)
  }, [])

  const activateCommand = React.useCallback(command => {
    if (!command) return

    closePalette()

    if (command.kind === "route") {
      navigate(command.to)
      return
    }

    if (command.kind === "event") {
      dispatchSiteEvent(command.eventName, command.fallbackPath)
      return
    }

    if (command.kind === "external") {
      window.open(command.href, "_blank", "noopener,noreferrer")
      return
    }

    if (command.kind === "download") {
      window.location.assign(command.href)
    }
  }, [closePalette])

  React.useEffect(() => {
    setMounted(true)
  }, [])

  React.useEffect(() => {
    const handleGlobalKeyDown = event => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault()
        openerRef.current = document.activeElement
        setOpen(current => !current)
      }
    }

    window.addEventListener("keydown", handleGlobalKeyDown)
    return () => window.removeEventListener("keydown", handleGlobalKeyDown)
  }, [])

  React.useEffect(() => {
    if (!open) return undefined
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    window.setTimeout(() => inputRef.current?.focus(), 0)
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [open])

  React.useEffect(() => {
    setActiveIndex(0)
  }, [query])

  const handleDialogKeyDown = event => {
    if (event.key === "Escape") {
      event.preventDefault()
      closePalette()
      return
    }

    if (event.key === "ArrowDown") {
      event.preventDefault()
      setActiveIndex(index => Math.min(index + 1, filteredCommands.length - 1))
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      setActiveIndex(index => Math.max(index - 1, 0))
      return
    }

    if (event.key === "Enter") {
      event.preventDefault()
      activateCommand(filteredCommands[activeIndex])
      return
    }

    if (event.key === "Tab") {
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll(
          "button, input, a, [tabindex]:not([tabindex='-1'])"
        ) || []
      ).filter(element => !element.disabled)

      if (!focusable.length) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
  }

  const palette = open ? (
    <div className="command-palette" onMouseDown={closePalette}>
      <div
        ref={dialogRef}
        className="command-palette__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="command-palette-title"
        onKeyDown={handleDialogKeyDown}
        onMouseDown={event => event.stopPropagation()}
      >
        <div className="command-palette__header">
          <div>
            <h2 id="command-palette-title">Command Palette</h2>
            <p>Jump, download, or open a site tool.</p>
          </div>
          <kbd>Esc</kbd>
        </div>
        <label className="command-palette__search">
          <Search size={18} aria-hidden="true" />
          <input
            id="command-palette-search"
            name="command-palette-search"
            ref={inputRef}
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search commands..."
            aria-label="Search commands"
          />
        </label>
        <div className="command-palette__list" role="listbox" aria-label="Commands">
          {filteredCommands.length ? (
            filteredCommands.map((command, index) => (
              <button
                key={command.id}
                type="button"
                className={index === activeIndex ? "is-active" : ""}
                role="option"
                aria-selected={index === activeIndex}
                onFocus={() => setActiveIndex(index)}
                onClick={() => activateCommand(command)}
              >
                <span className="command-palette__item-icon" aria-hidden="true">
                  {commandIcon(command)}
                </span>
                <span className="command-palette__item-copy">
                  <strong>{command.label}</strong>
                  <small>{command.group}</small>
                </span>
              </button>
            ))
          ) : (
            <p className="command-palette__empty">No commands found.</p>
          )}
        </div>
      </div>
    </div>
  ) : null

  return (
    <>
      <button
        type="button"
        className="command-palette-trigger"
        aria-label="Open command palette"
        title="Open command palette"
        data-glass-exclude
        onClick={openPalette}
      >
        <Command size={16} aria-hidden="true" />
        <span>Menu</span>
        <kbd>Cmd K</kbd>
      </button>
      {mounted ? createPortal(palette, document.body) : null}
    </>
  )
}

export default CommandPalette
